import { memo, type ReactNode, useMemo } from 'react';

import { Command } from '@air/cmdk';
import { type SearchBoardsResponse, type SearchSavedFiltersResponse, type SuggestionsResponse } from '@air/types';

import { usePrivateSearchUIHelpers } from '~/components/Search/hooks/usePrivateSearchUIHelpers';
import { SearchCommandBoardList } from '~/components/SearchCommand/SearchCommandBoardList';
import { SearchCommandSavedFilterList } from '~/components/SearchCommand/SearchCommandSavedFilterList';
import { SearchCommandSimilarImageSearch } from '~/components/SearchCommand/SearchCommandSimilarImageSearch';
import { SearchCommandSuggestionList } from '~/components/SearchCommand/SearchCommandSuggestionList';
export type SearchCommandListProps = {
  isOpen: boolean;
  hideSuggestions: boolean;
  searchValue?: string;
  suggestions?: SuggestionsResponse;
  renderCustomContent?: () => ReactNode;
  onSuggestionClick: (suggestion: string) => void;
  onSavedFilterSelect: (savedFilter: SearchSavedFiltersResponse) => void;
  onBoardClick: (board: SearchBoardsResponse) => void;
  onSimilarImageSearch: (base64Image: string) => void;
};

export const SearchCommandList = memo(
  ({
    isOpen,
    hideSuggestions,
    searchValue,
    suggestions,
    renderCustomContent,
    onSuggestionClick,
    onSavedFilterSelect,
    onBoardClick,
    onSimilarImageSearch,
  }: SearchCommandListProps) => {
    const { similarToImageSearchAvailable } = usePrivateSearchUIHelpers();

    const showSearchCommandList = useMemo(() => {
      const hasSearchTerm = searchValue && searchValue.trim().length > 0;
      const hasSuggestions = !!suggestions?.suggestions?.length;
      const hasSavedFilters = !!suggestions?.savedFilters?.length;
      const hasBoards = !!suggestions?.boards?.length;
      const hasSearchResults = hasSuggestions || hasSavedFilters || hasBoards;
      const hasCustomContent = !!renderCustomContent;

      const shouldShowForSearch = isOpen && !hideSuggestions && (hasSearchTerm || hasSearchResults);

      const shouldShowForCustomContent = isOpen && hasCustomContent;

      return shouldShowForSearch || shouldShowForCustomContent || similarToImageSearchAvailable;
    }, [isOpen, hideSuggestions, searchValue, suggestions, renderCustomContent, similarToImageSearchAvailable]);

    return showSearchCommandList ? (
      <div className="grow overflow-y-auto rounded-8 border border-grey-6 bg-grey-1 shadow-search-command">
        {renderCustomContent ? (
          renderCustomContent()
        ) : (
          <Command.List className="flex flex-col gap-0 px-4 py-3" data-testid="SEARCH_COMMAND_LIST">
            {!!onSuggestionClick && (
              <SearchCommandSuggestionList onSelect={onSuggestionClick} suggestions={suggestions?.suggestions} />
            )}

            {!!suggestions?.savedFilters.length && !!onSavedFilterSelect && (
              <SearchCommandSavedFilterList onSelect={onSavedFilterSelect} savedFilters={suggestions.savedFilters} />
            )}

            {!!suggestions?.boards.length && (
              <SearchCommandBoardList boards={suggestions.boards} onBoardClick={onBoardClick} />
            )}

            {similarToImageSearchAvailable && <SearchCommandSimilarImageSearch onSearch={onSimilarImageSearch} />}
          </Command.List>
        )}
      </div>
    ) : null;
  },
);

SearchCommandList.displayName = 'SearchCommandList';
